/**
 * useRoleAccess — the single definition of "beneficiary".
 *
 * Students and guardians are both beneficiaries of the initiative rather than
 * staff running it: read-only everywhere, and the staff-facing sections of a
 * screen (KPIs of a talent, development plans, coordinator and student tables)
 * are hidden from them entirely.
 *
 * This lived as a copy-pasted expression in six screens, and the screen that
 * never received the copy — StudentDetailScreen — was showing a parent the
 * whole staff toolbar. One definition, so the next screen cannot forget.
 *
 * Deliberately narrow: `readOnly` / `noCreate` stay in the screens, because
 * they differ per screen (some count superuser as strategy_team, some do not)
 * and folding them in here would silently change behaviour for other roles.
 */
import { useAuth } from '../contexts/AuthContext';
import { UserType } from '../types';

export interface RoleAccess {
  userType: UserType | null | undefined;
  isStudent: boolean;
  isGuardian: boolean;
  /** Student or guardian — read-only, staff sections hidden. */
  isReadOnlyBeneficiary: boolean;
}

export function useRoleAccess(): RoleAccess {
  const { user } = useAuth();
  const userType = user?.profile?.user_type;

  const isStudent = userType === 'student';
  const isGuardian = userType === 'guardian';

  return {
    userType,
    isStudent,
    isGuardian,
    isReadOnlyBeneficiary: isStudent || isGuardian,
  };
}
